import React, { useEffect, useState, useRef } from "react";
import { Formik } from "formik";
import { toast } from "react-toastify";
import SubTitle from "components/SubTitle";

import {
  useGetCitiesListQuery,
  useGetStreetsListQuery,
} from "features/api/constants/constantsApiSlice";
import {
  useGetApproximateReportLocationQuery,
  useAddOrUpdateeReportLocationByControllerMutation,
} from "features/api/reports/reportsSlice";

import { useTranslation } from "react-i18next";
import { City, Street } from "models/Constants";

import { Button, Spinner } from "react-bootstrap";
import { useRole } from "hooks/useRole";
import { useLocationForReportType } from "hooks/useLocationForReportType";
import { useReports } from "hooks/useReports";
import { FormStepsProps, LocationStepFormSchema } from "./FormStepsProps";
import {
  FormControlsValues,
  AddOrUpdateeReportLocationByControllerBody,
} from "./OffenseLocationStepComponents/OffenceStepTypes";

import LocationInputsFormSection from "./OffenseLocationStepComponents/LocationInputsFormSection";
import ButtonsSEctionReportDetails from "../buttonsSetionReportDetails";
import OffenseLocationMap from "./OffenseLocationStepComponents/OffenseLocationMap";
import LocationCommentTextArea from "./OffenseLocationStepComponents/LocationCommentTetArea";

const OffenseLocationStep = ({
  currStep,
  // control,
  // errors,
  setCurrStep,
  setModalShow,
  backNavigation,
}: FormStepsProps) => {
  const { t } = useTranslation();
  const formikRef = useRef<any>(null);
  const { data: cities } = useGetCitiesListQuery();

  const role = useRole();
  const locationString = useLocationForReportType();
  const isMessagesScreen = locationString === "followup-messages";
  const isReadonly = locationString === "followup" || isMessagesScreen;
  const { getReportId } = useReports();
  const reportId = getReportId();

  const { data: selectedReport }: { data: LocationStepFormSchema } =
    useGetApproximateReportLocationQuery(
      { reportId },
      {
        skip: !reportId,
        refetchOnMountOrArgChange: true,
      }
    );
  const [initialValues, setInitialValues] = useState(null);
  const [isBackNavigation, setIsBackNavigation] = useState(false);
  const [
    isMapCenterSetFromSelectedReport,
    setIsMapCenterSetFromSelectedReport,
  ] = useState(false);

  const [chosenCity, setChosenCity] =
    useState<{ value: number; label: string } | null>();
  const [chosenStreet, setChosenStreet] =
    useState<{ value: string; label: string } | null>();

  const { data: streets } = useGetStreetsListQuery(
    {
      cityCode: chosenCity?.value,
    },
    {
      skip: !chosenCity?.value,
      refetchOnMountOrArgChange: true,
    }
  );

  const [citiesOptions, setCitiesOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [streetsOptions, setStreetsOptions] = useState<
    { value: number; label: string }[]
  >([]);

  const [mapCenter, setMapCenter] =
    useState<{ lat: number; lng: number } | null>(null);

  const [addOrUpdateeReportLocationByController] =
    useAddOrUpdateeReportLocationByControllerMutation();

  // for setCitiesOptions & setStreetsOptions
  useEffect(() => {
    if (Array.isArray(cities?.cityList) && cities?.cityList.length > 0) {
      setCitiesOptions(
        cities.cityList.map((city: City) => ({
          label: city.cityName,
          value: city.cityCode,
        }))
      );
    }

    if (Array.isArray(streets?.streetList) && streets?.streetList.length > 0) {
      setStreetsOptions(
        streets.streetList.map((street: Street) => ({
          label: street.streetName,
          value: street.streetCode,
        }))
      );
    }
  }, [cities, streets, chosenCity]);

  // for setChosenCity
  useEffect(() => {
    if (selectedReport?.cityID && cities) {
      const selectedCity = cities?.cityList?.find(
        (city) => city.cityCode === selectedReport?.cityID
      );

      if (selectedCity !== undefined) {
        setChosenCity({
          label: selectedCity.cityName,
          value: selectedCity.cityCode,
        });
      }
    }
  }, [selectedReport, cities]);

  // for setChosenStreet
  useEffect(() => {
    if (selectedReport && streets) {
      if (chosenCity?.value === selectedReport?.cityID) {
        const selectedStreet = streets?.streetList?.find(
          (street) => selectedReport?.streetID === street?.streetCode
        );

        setChosenStreet(
          selectedStreet
            ? {
                label: selectedStreet.streetName,
                value: selectedStreet.streetCode,
              }
            : { label: t("chooseStreet"), value: "" }
        );
      } else {
        setChosenStreet({
          label: t("chooseStreet"),
          value: "",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport, streets, chosenCity]);

  // for setMapCenter by latitude && longitude
  useEffect(() => {
    if (selectedReport?.latitude && selectedReport?.longitude) {
      const flat = parseFloat(selectedReport?.latitude);
      const flng = parseFloat(selectedReport?.longitude);
      if (
        !!parseFloat(selectedReport?.latitude) &&
        !!parseFloat(selectedReport?.longitude)
      ) {
        setMapCenter({
          lat: flat,
          lng: flng,
        });
        setIsMapCenterSetFromSelectedReport(true);
      } else {
        setIsMapCenterSetFromSelectedReport(false);
      }
    }
  }, [selectedReport]);

  // for setInitialValues
  useEffect(() => {
    if (selectedReport) {
      const locationComment = selectedReport.locationComment || "";
      // TEMP-EMPTY-LOCATION: allow reports with no assigned city/street (cityID null,
      // non-interstate) to initialize the form with empty selectors instead of hanging
      // on the spinner. Without this the non-interstate branch only fired once
      // `chosenStreet` resolved, which never happens when there is no city.
      // TO REVERT: restore the original condition `!selectedReport.reportIsInterstate
      // && cities && chosenStreet` and `const street = chosenStreet;` below.
      if (
        !selectedReport.reportIsInterstate &&
        cities &&
        (!selectedReport.cityID || chosenStreet)
      ) {
        const city = {
          label: selectedReport?.cityID
            ? cities?.cityList?.find(
                (c) => c.cityCode === selectedReport?.cityID
              )?.cityName
            : t("chooseCity"),
          value: selectedReport?.cityID
            ? cities?.cityList?.find(
                (c) => c.cityCode === selectedReport?.cityID
              )?.cityCode
            : "",
        };
        const street = chosenStreet ?? { label: t("chooseStreet"), value: "" }; // TEMP-EMPTY-LOCATION
        const houseNumber = selectedReport.houseNumber ?? "";

        setInitialValues({
          ...selectedReport,
          locationComment,
          city,
          street,
          houseNumber,
          reportKm:
            selectedReport?.reportKm === null ? "" : selectedReport?.reportKm,
        });
      } else if (selectedReport.reportIsInterstate) {
        setInitialValues({
          ...selectedReport,
          reportKm:
            selectedReport?.reportKm === null ? "" : selectedReport?.reportKm,
          reportRoad:
            selectedReport?.reportRoad === null
              ? ""
              : selectedReport?.reportRoad,
          city: selectedReport?.city === null ? "" : selectedReport?.city,
          street: selectedReport?.street === null ? "" : selectedReport?.street,
          houseNumber:
            selectedReport?.houseNumber === null
              ? ""
              : selectedReport?.houseNumber,
          locationComment,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport, cities, chosenStreet]);

  const validateForm = (values: FormControlsValues) => {
    type errorObjectType = {
      city?: { value: string };
      street?: { value: string };
      reportRoad?: string;
      reportKm?: string;
      houseNumber?: string | number;
    };
    const errorsObject: errorObjectType = {};
    try {
      if (values?.reportIsInterstate === false) {
        if (!values?.city?.value) {
          formikRef.current.setFieldError("city", {
            value: t("validationRequired"),
          });
          errorsObject.city = { value: t("validationRequired") };
        } else {
          delete errorsObject?.city;
          formikRef.current.setErrors(errorsObject);
        }
        if (!values?.street?.value) {
          formikRef.current.setFieldError("street", {
            value: t("validationRequired"),
          });
          errorsObject.street = { value: t("validationRequired") };
        } else {
          delete errorsObject?.street;
          formikRef.current.setErrors(errorsObject);
        }
        if (values?.houseNumber?.toString().length > 4) {
          formikRef.current.setFieldError(
            "houseNumber",
            t("validationHouseNumberMax4Digits")
          );
          errorsObject.houseNumber = t("validationHouseNumberMax4Digits");
        } else {
          delete errorsObject?.houseNumber;
          formikRef.current.setErrors(errorsObject);
        }
      } else {
        if (!values?.reportRoad?.length) {
          formikRef.current.setFieldError(
            "reportRoad",
            t("validationRequired")
          );
          errorsObject.reportRoad = t("validationRequired");
        } else if (values?.reportRoad?.length > 20) {
          formikRef.current.setFieldError(
            "reportRoad",
            t("validationRoadNameMax20Chars")
          );
          errorsObject.reportRoad = t("validationRoadNameMax20Chars");
        } else {
          delete errorsObject?.reportRoad;
          formikRef.current.setErrors(errorsObject);
        }
        if (values?.reportKm?.toString().length > 40) {
          formikRef.current.setFieldError(
            "reportKm",
            t("validationReportLocationKmMax40Digits")
          );
          errorsObject.reportKm = t("validationReportLocationKmMax40Digits");
        } else {
          delete errorsObject?.reportKm;
          formikRef.current.setErrors(errorsObject);
        }
      }
      return errorsObject;
    } catch (e) {
      throw new Error(e);
    }
  };

  const formikPropsObject = {
    onSubmit: async (values, { setSubmitting }) => {
      if (isReadonly) {
        isBackNavigation ? backNavigation() : setCurrStep(currStep + 1);
        return;
      }

      const validationObject = validateForm(values);

      if (Object.keys(validationObject).length === 0) {
        const body: AddOrUpdateeReportLocationByControllerBody = {
          ReportId: selectedReport.reportID,
          ReportIsInterstate:
            values?.reportIsInterstate ?? selectedReport?.reportIsInterstate,
          ReportRoad: values?.reportIsInterstate ? values?.reportRoad : "",
          ReportKm: values?.reportIsInterstate ? values?.reportKm : "",
          Longitude: values.Longitude ?? selectedReport.longitude,
          Latitude: values.Latitude ?? selectedReport.latitude,
          CityId: values?.reportIsInterstate ? "" : values?.city?.value,
          StreetId: values?.reportIsInterstate ? "" : values?.street?.value,
          houseNumber: values?.reportIsInterstate ? "" : values?.houseNumber,
          locationComment: values?.locationComment ?? "",
        };

        // submit to server
        addOrUpdateeReportLocationByController(body).then((resp) => {
          if (resp?.error?.data?.[0]?.errorMessage) {
            toast.error(resp.error.data[0].errorMessage);
            return;
          }
          isBackNavigation ? backNavigation() : setCurrStep((step) => step + 1);
        });
      } else {
        return;
      }
      setSubmitting(false);
    },
  };

  return initialValues ? (
    <>
      <SubTitle className="default-vertical-padding">
        {t("wizardStep2Title")}
      </SubTitle>
      <div
        style={{
          minHeight: "300px",
          minWidth: "100%",
        }}
      >
        <OffenseLocationMap incidentCoords={mapCenter} />
      </div>
      <div className="text-center mb-3">
        <Button
          variant="outline-primary"
          onClick={() =>
            window.open(
              "https://www.govmap.gov.il/?c=204000,595000&z=0",
              "_blank"
            )
          }
          style={{ width: "auto", display: "inline-block" }}
        >
          {t("israelMapsGovmap")}
        </Button>
      </div>

      <Formik
        initialValues={initialValues}
        innerRef={formikRef}
        {...formikPropsObject}
      >
        {(formikProps) => (
          <>
            <LocationCommentTextArea formikProps={formikProps} />

            <LocationInputsFormSection
              formikProps={formikProps}
              citiesOptions={citiesOptions}
              streetsOptions={streetsOptions}
              setChosenCity={setChosenCity}
              setMapCenter={setMapCenter}
              isMapCenterSetFromSelectedReport={
                isMapCenterSetFromSelectedReport
              }
              setIsMapCenterSetFromSelectedReport={
                setIsMapCenterSetFromSelectedReport
              }
            />

            {!isMessagesScreen && (
              <ButtonsSEctionReportDetails
                handleSubmit={formikProps?.handleSubmit}
                setModalShow={setModalShow}
                setIsBackNavigation={setIsBackNavigation}
                currStep={currStep}
              />
            )}
          </>
        )}
      </Formik>
    </>
  ) : (
    <Spinner />
  );
};

export default OffenseLocationStep;
